import React, { useEffect, useRef, useState } from 'react';

import { SignatureCommit, SignatureState } from '../types';
import styles from './SignaturePad.module.scss';

type Props = {
  mode: 'draw' | 'type';
  onModeChange: (mode: 'draw' | 'type') => void;
  onChange: (hasSig: boolean) => void;
  onCommit: (commit: SignatureCommit) => void;
  onApply?: (dataUrl: string) => void;
  disabled?: boolean;
  preview?: boolean;
  isHost?: boolean;
  isMobilePreview?: boolean;
  externalSignature?: SignatureState | null;
  clientLabel?: string;
  padMode?: 'signature' | 'initials';
  preloadDataUrl?: string;
  preloadTyped?: string;
};

export function SignaturePad({
  mode,
  onModeChange,
  onChange,
  onCommit,
  onApply,
  disabled = false,
  preview = false,
  isHost = false,
  isMobilePreview = false,
  externalSignature,
  clientLabel = 'Patient',
  padMode = 'signature',
  preloadDataUrl,
  preloadTyped,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const typedRef = useRef<HTMLInputElement>(null);
  const drawingRef = useRef(false);
  const strokesRef = useRef<{ x: number; y: number }[][]>([]);
  const [strokeCount, setStrokeCount] = useState(0);
  const [typed, setTyped] = useState('');
  const [applied, setApplied] = useState(false);
  const [hasPreload, setHasPreload] = useState(false);
  // Suppresses re-drawing preload after the user explicitly switches mode or clears
  const preloadSuppressedRef = useRef(false);

  const isLocked = disabled || preview || isHost || isMobilePreview;

  // Load Caveat font for typed signature rendering
  useEffect(() => {
    const id = 'caveat-font-link';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const setupCtx = (ctx: CanvasRenderingContext2D) => {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0F1A2A';
    ctx.lineWidth = 2.2;
  };

  const drawStrokeSmooth = (ctx: CanvasRenderingContext2D, stroke: { x: number; y: number }[]) => {
    if (stroke.length === 1) {
      ctx.beginPath();
      ctx.arc(stroke[0].x, stroke[0].y, 1.1, 0, Math.PI * 2);
      ctx.fillStyle = '#0F1A2A';
      ctx.fill();
      return;
    }
    if (stroke.length === 2) {
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      ctx.lineTo(stroke[1].x, stroke[1].y);
      ctx.stroke();
      return;
    }
    ctx.beginPath();
    ctx.moveTo((stroke[0].x + stroke[1].x) / 2, (stroke[0].y + stroke[1].y) / 2);
    for (let i = 1; i < stroke.length - 1; i++) {
      const mid = { x: (stroke[i].x + stroke[i + 1].x) / 2, y: (stroke[i].y + stroke[i + 1].y) / 2 };
      ctx.quadraticCurveTo(stroke[i].x, stroke[i].y, mid.x, mid.y);
    }
    ctx.lineTo(stroke[stroke.length - 1].x, stroke[stroke.length - 1].y);
    ctx.stroke();
  };

  const redraw = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.scale(dpr, dpr);
    setupCtx(ctx);
    for (const stroke of strokesRef.current) {
      if (stroke.length < 1) continue;
      drawStrokeSmooth(ctx, stroke);
    }
    ctx.restore();
  };

  const drawPreloadToCanvas = () => {
    if (!preloadDataUrl || strokesRef.current.length > 0 || preloadSuppressedRef.current) return;
    const c = canvasRef.current;
    if (!c) return;
    const img = new Image();
    img.onload = () => {
      const dpr = window.devicePixelRatio || 1;
      const ctx = c.getContext('2d')!;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(img, 0, 0, c.width / dpr, c.height / dpr);
      ctx.restore();
      setHasPreload(true);
      onChange(true);
    };
    img.src = preloadDataUrl;
  };

  const getCompressedDataUrl = (): string => {
    if (strokesRef.current.length === 0 && hasPreload && preloadDataUrl) return preloadDataUrl;
    const c = canvasRef.current;
    if (!c || strokesRef.current.length === 0) return '';
    // Full HiDPI canvas → crisp PNG on host mirror and PDF
    return c.toDataURL('image/png');
  };

  const commitDraw = (cleared = false) => {
    const c = canvasRef.current;
    onCommit({
      kind: 'draw',
      dataUrl: cleared || strokesRef.current.length === 0 ? '' : c ? c.toDataURL('image/png') : '',
    });
  };

  useEffect(() => {
    if (mode !== 'draw') return;
    const c = canvasRef.current;
    if (!c) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const r = c.getBoundingClientRect();
      c.width = r.width * dpr;
      c.height = r.height * dpr;
      const ctx = c.getContext('2d')!;
      ctx.scale(dpr, dpr);
      setupCtx(ctx);
      redraw();
      drawPreloadToCanvas();
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [mode]);

  // Reset suppression flag whenever a new preload URL arrives (modal reopened)
  useEffect(() => {
    preloadSuppressedRef.current = false;
    setHasPreload(false);
  }, [preloadDataUrl]);

  // Pre-populate typed field when reopening in type mode with prior initials
  useEffect(() => {
    if (!preloadTyped) return;
    setTyped(preloadTyped);
    onChange(preloadTyped.trim().length > 1);
  }, [preloadTyped]); // eslint-disable-line

  // Auto-focus the type input when switching to type mode
  useEffect(() => {
    if (mode === 'type' && !isLocked) {
      typedRef.current?.focus();
    }
  }, [mode, isLocked]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (isLocked) return;
    e.preventDefault();
    drawingRef.current = true;
    canvasRef.current!.setPointerCapture(e.pointerId);
    const p = pos(e);
    strokesRef.current.push([p]);
    setStrokeCount(strokesRef.current.length);
    if (strokesRef.current.length === 1) onChange(true);
  };

  const onMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    const p = pos(e);
    stroke.push(p);
    const n = stroke.length;
    const ctx = canvasRef.current!.getContext('2d')!;
    setupCtx(ctx);
    if (n >= 3) {
      const p0 = stroke[n - 3];
      const p1 = stroke[n - 2];
      const p2 = stroke[n - 1];
      const mid1 = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
      const mid2 = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      ctx.beginPath();
      ctx.moveTo(mid1.x, mid1.y);
      ctx.quadraticCurveTo(p1.x, p1.y, mid2.x, mid2.y);
      ctx.stroke();
    } else if (n === 2) {
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
  };

  const onUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    commitDraw();
  };

  const undo = () => {
    if (strokesRef.current.length === 0) return;
    strokesRef.current.pop();
    setStrokeCount(strokesRef.current.length);
    redraw();
    if (strokesRef.current.length === 0) {
      onChange(false);
      commitDraw(true);
    } else {
      commitDraw();
    }
  };

  const clear = () => {
    setApplied(false);
    setHasPreload(false);
    preloadSuppressedRef.current = true;
    if (mode === 'draw') {
      strokesRef.current = [];
      setStrokeCount(0);
      redraw();
      onChange(false);
      commitDraw(true);
    } else {
      setTyped('');
      onChange(false);
      onCommit({ kind: 'type', text: '' });
    }
  };

  const onTyped = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setTyped(v);
    onChange(v.trim().length > 1);
    onCommit({ kind: 'type', text: v });
  };

  const hasExternalSig = !!(externalSignature?.dataUrl || externalSignature?.text);
  const displayMode = isHost && !hasExternalSig ? 'draw' : mode;

  const hasInk = displayMode === 'draw' ? strokeCount > 0 || hasPreload : typed.trim().length > 0;
  const canUndo = displayMode === 'draw' && strokeCount > 0;

  if (preview) {
    return (
      <div className={styles.sigWrap}>
        <div className={styles.sigHeader}>
          <span className={styles.sigHeaderDot} />
          <span>{padMode === 'initials' ? 'Initials required' : 'Signature required'}</span>
          <span className={styles.sigHeaderSpacer} />
          <span className={styles.sigHeaderHint}>Host view — locked</span>
        </div>

        <div className={styles.sigTabs} role='tablist'>
          <button type='button' className={styles.active} disabled>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path d='M3 21l3.5-1 11-11-2.5-2.5-11 11L3 21z' />
              <path d='M14 6l4 4' />
            </svg>
            Draw
          </button>
          <button type='button' disabled>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path d='M5 7V5h14v2' />
              <path d='M12 5v14' />
              <path d='M9 19h6' />
            </svg>
            Type
          </button>
        </div>

        <div className={`${styles.sigCanvasWrap} ${styles.locked}`}>
          <div className={styles.sigBaseline} />
          <div className={styles.sigX}>×</div>
          <div className={styles.sigCaret} aria-hidden='true'>
            <span className={styles.sigCaretFlag}>{padMode === 'initials' ? 'Initial here' : 'Sign here'}</span>
          </div>
          {padMode !== 'initials' && (
            <div className={styles.sigPlaceholder}>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <path d='M3 21l3.5-1 11-11-2.5-2.5-11 11L3 21z' />
              </svg>
              Sign with your finger or mouse
            </div>
          )}
          <div className={styles.previewOverlay}>
            <div className={styles.previewPlaceholder}>
              {padMode === 'initials' ? 'Initials required — preview only' : 'Signature required — preview only'}
            </div>
          </div>
        </div>

        <div className={styles.sigFoot}>
          <div className={styles.meta}>
            <span className={styles.lock}>
              <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <rect x='4' y='11' width='16' height='10' rx='2' />
                <path d='M8 11V7a4 4 0 018 0v4' />
              </svg>
            </span>
            <span>Encrypted · IP &amp; timestamp captured on signing</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.sigWrap}>
      <div className={styles.sigHeader}>
        <span className={styles.sigHeaderDot} />
        <span>{padMode === 'initials' ? 'Initials required' : 'Required signature'}</span>
        <span className={styles.sigHeaderSpacer} />
        {isHost && <span className={styles.sigHeaderHint}>Host view — locked</span>}
      </div>

      <div className={styles.sigTabs} role='tablist'>
        <button
          type='button'
          className={displayMode === 'draw' ? styles.active : ''}
          onClick={() => {
            if (isLocked) return;
            preloadSuppressedRef.current = true;
            setHasPreload(false);
            setTyped('');
            setApplied(false);
            onChange(strokesRef.current.length > 0);
            onModeChange('draw');
          }}
          disabled={isLocked}
        >
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <path d='M3 21l3.5-1 11-11-2.5-2.5-11 11L3 21z' />
            <path d='M14 6l4 4' />
          </svg>
          Draw
        </button>
        <button
          type='button'
          className={displayMode === 'type' ? styles.active : ''}
          onClick={() => {
            if (isLocked) return;
            preloadSuppressedRef.current = true;
            setHasPreload(false);
            strokesRef.current = [];
            setStrokeCount(0);
            redraw();
            setApplied(false);
            onChange(typed.trim().length > 1);
            onModeChange('type');
          }}
          disabled={isLocked}
        >
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <path d='M5 7V5h14v2' />
            <path d='M12 5v14' />
            <path d='M9 19h6' />
          </svg>
          Type
        </button>
      </div>

      <div
        className={`${styles.sigCanvasWrap} ${hasInk ? styles.hasInk : ''} ${isLocked ? styles.locked : ''}`}
        style={isHost && hasExternalSig ? { background: '#fff', opacity: 1 } : undefined}
      >
        {isHost && !hasExternalSig && (
          <div className={styles.hostBanner}>
            <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2'>
              <circle cx='12' cy='12' r='10' />
              <path d='M12 8v4' />
              <path d='M12 16h.01' />
            </svg>
            <span>
              <strong>Tell your {clientLabel.toLowerCase()}:</strong> Sign in the yellow area, then click{' '}
              <strong>&ldquo;Accept Signature&rdquo;</strong> to continue.
            </span>
          </div>
        )}
        {displayMode === 'draw' ? (
          <>
            <canvas
              ref={canvasRef}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              style={{ cursor: isLocked ? 'not-allowed' : 'crosshair' }}
            />
            <div className={styles.sigX}>×</div>
            {!(isHost && externalSignature?.dataUrl) && (
              <div className={styles.sigCaret} aria-hidden='true'>
                <span className={styles.sigCaretFlag}>{padMode === 'initials' ? 'Initial here' : 'Sign here'}</span>
              </div>
            )}
            <div className={styles.sigBaseline} />
            {isHost ? (
              externalSignature?.dataUrl ? (
                <img
                  src={externalSignature.dataUrl}
                  alt='Client signature'
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    padding: '8px',
                  }}
                />
              ) : null
            ) : (
              strokeCount === 0 &&
              !hasPreload &&
              padMode !== 'initials' && (
                <div
                  className={styles.sigPlaceholder}
                  style={
                    isMobilePreview ? { position: 'relative', left: '65%', top: 'auto', transform: 'none' } : undefined
                  }
                >
                  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                    <path d='M3 21l3.5-1 11-11-2.5-2.5-11 11L3 21z' />
                  </svg>
                  Sign with your finger or mouse
                </div>
              )
            )}
          </>
        ) : (
          <>
            <div className={styles.sigX}>×</div>
            <div className={styles.sigBaseline} />
            {isHost && externalSignature?.text ? (
              <span className={styles.sigTyped} style={{ pointerEvents: 'none' }}>
                {externalSignature.text}
              </span>
            ) : isHost ? null : (
              <input
                ref={typedRef}
                className={styles.sigTyped}
                placeholder={padMode === 'initials' ? 'Type your initials' : 'Type your full name'}
                value={typed}
                onChange={onTyped}
                maxLength={48}
                disabled={isLocked}
              />
            )}
          </>
        )}
      </div>

      <div className={styles.sigFoot}>
        <div className={styles.meta}>
          <span className={styles.lock}>
            <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <rect x='4' y='11' width='16' height='10' rx='2' />
              <path d='M8 11V7a4 4 0 018 0v4' />
            </svg>
          </span>
          {!isMobilePreview && padMode !== 'initials' && (
            <span>Encrypted · IP &amp; timestamp captured on signing</span>
          )}
        </div>
        {!isHost && (
          <div className={styles.sigActions}>
            {mode === 'draw' && (
              <button type='button' onClick={undo} disabled={!canUndo || isLocked}>
                <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='M3 7v6h6' />
                  <path d='M21 17a9 9 0 00-15-6.7L3 13' />
                </svg>
                Undo
              </button>
            )}
            <button type='button' onClick={clear} disabled={!hasInk || isLocked}>
              Clear
            </button>
            {hasInk && (
              <button
                type='button'
                className={applied ? styles.sigAppliedBtn : styles.sigApplyBtn}
                disabled={applied}
                onClick={() => {
                  if (mode === 'draw') {
                    onApply?.(getCompressedDataUrl());
                    commitDraw();
                  } else {
                    onApply?.(typed);
                  }
                  setApplied(true);
                }}
              >
                {applied ? (
                  <>
                    <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                      <path d='M5 13l4 4L19 7' />
                    </svg>
                    Applied
                  </>
                ) : padMode === 'initials' ? (
                  'Apply Initials'
                ) : (
                  'Apply Signature'
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
